import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '@/components/Button';
import Spinner from '@/components/Spinner';
import useApp from '@/pages/App.context';
import { isWebidOutcome } from '@/pages/Kyc/webidForm.types';
import useWebidStatusPoll from './useWebidStatusPoll';
import styles from './WebidReturn.module.scss';

/**
 * WebID identity-gate return page for a guest signer. WebID redirects here after
 * the video-ident finishes. We poll the webhook-set webidVerified flag (via
 * useWebidStatusPoll), then send the signer back to the document to swipe again
 * — the swipe re-enters SIGNING and (webidVerified being true) auto-starts
 * A-Trust.
 *
 * The redirect carries ?webid=success|declined|mismatch|cancelled; any
 * non-success outcome is a terminal failure (the signer can retry from the
 * document).
 */
const WebidReturn = () => {
  const { t } = useTranslation(['sign']);
  const { setAppTitle } = useApp();
  const navigate = useNavigate();
  const { workflowId, signerId } = useParams();
  const [searchParams] = useSearchParams();

  const outcome = searchParams.get('webid');
  // A known non-success outcome (declined/mismatch/cancelled) is terminal. A
  // missing/unknown param is treated as a success return — the redirect always
  // carries one, but be lenient.
  const outcomeFailed = isWebidOutcome(outcome) && outcome !== 'success';
  const documentUrl = `/sign/${workflowId}/${signerId}/document`;

  const { verified, timedOut } = useWebidStatusPoll({
    workflowId,
    enabled: !outcomeFailed,
  });
  const failed = outcomeFailed || timedOut;

  useEffect(() => {
    setAppTitle(t('sign:webid.verifyingTitle'));
  }, [setAppTitle, t]);

  // Once verified, send the signer back to the document to re-swipe and resume.
  useEffect(() => {
    if (verified) {
      void navigate(documentUrl, { replace: true });
    }
  }, [verified, navigate, documentUrl]);

  if (failed) {
    return (
      <div className={styles.pane}>
        <h1 className={styles.title}>{t('sign:webid.failedTitle')}</h1>
        <div className={styles.desc}>{t('sign:webid.failedDesc')}</div>
        <Button
          as="link"
          to={documentUrl}
          replace
          className={styles.cta}
          variant="primary"
        >
          {t('sign:webid.failedRetry')}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.pane}>
      <div
        className={styles.loaderWrap}
        role="status"
        aria-label={t('sign:webid.verifying')}
      >
        <Spinner />
      </div>
      <h1 className={styles.title}>{t('sign:webid.verifyingTitle')}</h1>
      <div className={styles.desc}>{t('sign:webid.verifyingDesc')}</div>
    </div>
  );
};

export default WebidReturn;
